import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Loader } from './Icons';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const XIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Per-page font sizes: { [pageNum]: fontSize }
// Falls back to global pageNumberFontSize if not set for that page
const DEFAULT_FONT_SIZE = 26;

export default function PDFEditorCanvas({
  fileUrl, scale, pageRotations, selectedPage, onPageClick, onDocumentLoadSuccess,
  onZoom, sessionId, overlays, currentFilename, selectedOverlayId,
  onSelectOverlay, onUpdateOverlay, onDeleteOverlay,
  pageNumberPosition = {}, setPageNumberPosition, getPageNumberPosition,
  pageNumberFontSize = 26, setPageNumberFontSize,
  pageNumberPositionsMap,
  showPageNumbers = true, pageOffset = 0,
  apiBaseUrl
}) {
  const canvasRef = useRef(null);
  const pageRefs = useRef({});
  const [numPages, setNumPages] = useState(null);
  const isScrollingProgrammatically = useRef(false);

  // Overlay drag/resize states
  const [isDraggingPageNum, setIsDraggingPageNum] = useState(false);
  const draggingPageRect = useRef(null);
  const draggingPageNumRef = useRef(null);
  const draggingOverlayPageRect = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, startFontSize: 20 });

  // Page number selection + resize states
  const [selectedPageNum, setSelectedPageNum] = useState(null);
  const [isResizingPageNum, setIsResizingPageNum] = useState(false);
  const [pageNumResizeHandle, setPageNumResizeHandle] = useState(null);
  const pageNumResizeStart = useRef({
    mouseX: 0, mouseY: 0,
    startFontSize: DEFAULT_FONT_SIZE,
    startX: 0, startY: 0,
    startW: 0, startH: 0,
    pageRect: null,
  });

  // Per-page font sizes stored locally (mirrors the global for simplicity)
  // We use pageNumberFontSize as a single global for all pages (existing behaviour)
  // but expose a per-page resize that calls setPageNumberFontSize
  const activeFilename = currentFilename;

  const getPosForPage = (pageNum) => {
    // Direct lookup into the map — never stale
    if (pageNumberPositionsMap && currentFilename) {
      return pageNumberPositionsMap[currentFilename]?.[pageNum] || { x: 95, y: 4 };
    }
    return { x: 95, y: 4 };
  };

  const handleDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    if (onDocumentLoadSuccess) onDocumentLoadSuccess({ numPages: pages });
  };

  // Scroll to selected page
  useEffect(() => {
    if (selectedPage && pageRefs.current[selectedPage] && !isScrollingProgrammatically.current) {
      isScrollingProgrammatically.current = true;
      pageRefs.current[selectedPage].scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => { isScrollingProgrammatically.current = false; }, 1000);
    }
  }, [selectedPage]);

  // Deselect page number label when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => setSelectedPageNum(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  // Intersection Observer
  useEffect(() => {
    if (!canvasRef.current || !numPages) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingProgrammatically.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const pageNum = parseInt(entry.target.dataset.pageNum);
            if (pageNum && pageNum !== selectedPage) onPageClick(pageNum);
          }
        });
      },
      { root: canvasRef.current, threshold: 0.5, rootMargin: '-10% 0px -10% 0px' }
    );
    Object.values(pageRefs.current).forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [numPages, selectedPage, onPageClick]);

  // Global mouse listeners
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();

      // 1. OVERLAY DRAGGING
      if (isDragging && selectedOverlayId && draggingOverlayPageRect.current) {
        const pageRect = draggingOverlayPageRect.current;
        const overlay = overlays.find(o => o.id === selectedOverlayId);
        if (overlay) {
          const rawX = (e.clientX - pageRect.left) / pageRect.width;
          const rawY = (e.clientY - pageRect.top) / pageRect.height;
          const width = overlay.width || 0.15;
          const height = overlay.height || 0.05;
          onUpdateOverlay(selectedOverlayId, { x: rawX - width / 2, y: rawY - height / 2 }, false);
        }
      } else if (isDragging && selectedOverlayId && !draggingOverlayPageRect.current) {
        const newX = (e.clientX - rect.left) / rect.width;
        const newY = (e.clientY - rect.top) / rect.height;
        onUpdateOverlay(selectedOverlayId, {
          x: Math.max(0, Math.min(1, newX)),
          y: Math.max(0, Math.min(1, newY)),
        }, false);
      }

      // 2. PAGE NUMBER DRAGGING
      if (isDraggingPageNum && setPageNumberPosition && draggingPageRect.current && draggingPageNumRef.current) {
        const pageRect = draggingPageRect.current;
        const rawX = ((e.clientX - pageRect.left) / pageRect.width) * 100;
        const rawY = ((e.clientY - pageRect.top) / pageRect.height) * 100;
        setPageNumberPosition(draggingPageNumRef.current, {
          x: Math.max(0, Math.min(100, rawX)),
          y: Math.max(0, Math.min(100, rawY)),
        });
      }

      // 3. OVERLAY RESIZING
      if (isResizing && selectedOverlayId && resizeHandle && draggingOverlayPageRect.current) {
        const pageRect = draggingOverlayPageRect.current;
        const { x: startX, y: startY, width: startW, height: startH } = resizeStart;
        const rightBoundary = startX + startW;
        const bottomBoundary = startY + startH;
        const mouseX = (e.clientX - pageRect.left) / pageRect.width;
        const mouseY = (e.clientY - pageRect.top) / pageRect.height;
        let newX = startX, newY = startY, newWidth = startW, newHeight = startH;
        const minSize = 0.02;

        if (resizeHandle.includes('e')) { newWidth = Math.max(minSize, mouseX - startX); newX = startX; }
        else if (resizeHandle.includes('w')) { newWidth = Math.max(minSize, rightBoundary - mouseX); newX = rightBoundary - newWidth; }
        if (resizeHandle.includes('s')) { newHeight = Math.max(minSize, mouseY - startY); newY = startY; }
        else if (resizeHandle.includes('n')) { newHeight = Math.max(minSize, bottomBoundary - mouseY); newY = bottomBoundary - newHeight; }

        const updates = { width: newWidth, height: newHeight, x: newX, y: newY };
        const resizingOverlay = overlays.find(o => o.id === selectedOverlayId);
        if (resizingOverlay?.type === 'text') {
          const scaleFactor = newWidth / resizeStart.width;
          updates.fontSize = Math.round(Math.max(8, resizeStart.startFontSize * scaleFactor));
        }
        onUpdateOverlay(selectedOverlayId, updates, false);
      }

      // 4. PAGE NUMBER RESIZING (4-corner, font scales with width)
      if (isResizingPageNum && pageNumResizeHandle && pageNumResizeStart.current.pageRect) {
        const pageRect = pageNumResizeStart.current.pageRect;
        const { startX, startY, startW, startH, startFontSize } = pageNumResizeStart.current;
        const mouseX = (e.clientX - pageRect.left) / pageRect.width * 100;
        const mouseY = (e.clientY - pageRect.top) / pageRect.height * 100;
        const minSize = 2;

        let newW = startW, newH = startH, newX = startX, newY = startY;

        if (pageNumResizeHandle.includes('e')) { newW = Math.max(minSize, mouseX - startX); }
        else if (pageNumResizeHandle.includes('w')) { newW = Math.max(minSize, (startX + startW) - mouseX); newX = (startX + startW) - newW; }
        if (pageNumResizeHandle.includes('s')) { newH = Math.max(minSize, mouseY - startY); }
        else if (pageNumResizeHandle.includes('n')) { newH = Math.max(minSize, (startY + startH) - mouseY); newY = (startY + startH) - newH; }

        // Scale font proportionally to width change
        const scaleFactor = newW / startW;
        const newFontSize = Math.round(Math.max(8, Math.min(120, startFontSize * scaleFactor)));
        setPageNumberFontSize(newFontSize);

        // Also update position to follow resize direction
        if (draggingPageNumRef.current) {
          setPageNumberPosition(draggingPageNumRef.current, {
            x: Math.max(0, Math.min(100, newX + newW / 2)),
            y: Math.max(0, Math.min(100, newY + newH / 2)),
          });
        }
      }
    };

    const handleMouseUp = () => {
      if ((isDragging || isResizing) && selectedOverlayId) {
        const currentOverlay = overlays.find(o => o.id === selectedOverlayId);
        if (currentOverlay) onUpdateOverlay(selectedOverlayId, { ...currentOverlay }, true);
      }
      setIsDraggingPageNum(false);
      draggingPageRect.current = null;
      draggingPageNumRef.current = null;
      draggingOverlayPageRect.current = null;
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      setIsResizingPageNum(false);
      setPageNumResizeHandle(null);
    };

    if (isDraggingPageNum || isDragging || isResizing || isResizingPageNum) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDraggingPageNum, isDragging, isResizing, isResizingPageNum,
    selectedOverlayId, overlays, onUpdateOverlay,
    setPageNumberPosition, setPageNumberFontSize,
    resizeHandle, resizeStart, pageNumResizeHandle,
  ]);

  const handleResizeStart = (e, handle, overlay) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({
      x: overlay.x,
      y: overlay.y,
      width: overlay.width || 0.2,
      height: overlay.height || 0.05,
      startFontSize: overlay.fontSize || 20,
    });
    const pageWrapper = e.currentTarget.closest('.page-wrapper');
    if (pageWrapper) draggingOverlayPageRect.current = pageWrapper.getBoundingClientRect();
  };

  // Starts a page-number resize from one of 4 corners
  // Uses actual DOM measurement so handles sit exactly on the label edges
  const handlePageNumResizeStart = (e, handle, pageNum, labelEl) => {
    e.stopPropagation();
    e.preventDefault();

    const pageWrapper = pageRefs.current[pageNum];
    if (!pageWrapper || !labelEl) return;

    const pageRect  = pageWrapper.getBoundingClientRect();
    const labelRect = labelEl.getBoundingClientRect();

    // Convert label rect to % of page
    const x = ((labelRect.left   - pageRect.left)  / pageRect.width)  * 100;
    const y = ((labelRect.top    - pageRect.top)   / pageRect.height) * 100;
    const w = (labelRect.width   / pageRect.width)  * 100;
    const h = (labelRect.height  / pageRect.height) * 100;

    setIsResizingPageNum(true);
    setPageNumResizeHandle(handle);
    draggingPageNumRef.current = pageNum;
    pageNumResizeStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startFontSize: pageNumberFontSize,
      startX: x,
      startY: y,
      startW: Math.max(w, 2),
      startH: Math.max(h, 1),
      pageRect,
    };
  };

  const resizeHandleDot = (cursor, style = {}) => ({
    position: 'absolute',
    width: '10px',
    height: '10px',
    background: 'white',
    border: '2px solid #3b82f6',
    borderRadius: '50%',
    cursor,
    zIndex: 1000,
    ...style,
  });

  return (
    <div
      className="pdf-editor-canvas"
      ref={canvasRef}
      onMouseDown={() => { onSelectOverlay(null); setSelectedPageNum(null); }}
      tabIndex={0}
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        position: 'relative',
        background: 'transparent',
      }}
    >
      <Document
        key={fileUrl}
        file={fileUrl}
        onLoadSuccess={handleDocumentLoadSuccess}
        loading={<Loader size={48} className="spin-anim" />}
      >
        {numPages && Array.from(new Array(numPages), (el, index) => {
          const pageNum = index + 1;
          const thisPagePos = getPosForPage(pageNum);
          const isPageNumSelected = selectedPageNum === pageNum;

          return (
            <div
              key={`page_${pageNum}`}
              ref={(el) => (pageRefs.current[pageNum] = el)}
              data-page-num={pageNum}
              className="page-wrapper"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                marginBottom: '20px',
                position: 'relative',
                outline: selectedPage === pageNum ? '3px solid #3b82f6' : 'none',
                outlineOffset: '5px',
              }}
              onMouseDown={() => onSelectOverlay(null)}
              onClick={() => onPageClick(pageNum)}
            >
              <Page
                pageNumber={pageNum}
                rotate={pageRotations[pageNum] || 0}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                className="pdf-page-shadow"
                width={600}
              />

              {/* ── Page Number Label ── */}
              {showPageNumbers && (() => {
                // labelRef lets resize handler measure the actual rendered element
                const labelRef = { current: null };
                return (
                  <div
                    className="canvas-page-number-wrapper"
                    style={{
                      left: `${thisPagePos.x}%`,
                      top: `${thisPagePos.y}%`,
                      transform: 'translate(-50%, -50%)',
                      position: 'absolute',
                      zIndex: 20,
                      userSelect: 'none',
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedPageNum(pageNum); }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onPageClick(pageNum);
                      setSelectedPageNum(pageNum);
                      const pw = pageRefs.current[pageNum];
                      if (pw) draggingPageRect.current = pw.getBoundingClientRect();
                      draggingPageNumRef.current = pageNum;
                      setIsDraggingPageNum(true);
                    }}
                  >
                    {/* Number text — ref captured for resize measurement */}
                    <div
                      ref={(el) => { labelRef.current = el; }}
                      className="canvas-page-number-text"
                      style={{
                        fontSize: `${pageNumberFontSize}px`,
                        fontWeight: '400',
                        color: '#333',
                        backgroundColor: isPageNumSelected
                          ? 'rgba(59,130,246,0.08)'
                          : 'rgba(255,255,255,0.9)',
                        padding: '6px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: isPageNumSelected
                          ? '1.5px solid #3b82f6'
                          : '1.5px solid transparent',
                        cursor: isDraggingPageNum ? 'grabbing' : 'grab',
                        transition: 'border 0.15s, background 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pageNum + pageOffset}
                    </div>

                    {/* 4-corner resize handles — only when selected */}
                    {isPageNumSelected && (
                      <>
                        <div
                          style={resizeHandleDot('nwse-resize', { top: '-5px', left: '-5px' })}
                          onMouseDown={(e) => handlePageNumResizeStart(e, 'nw', pageNum, labelRef.current)}
                        />
                        <div
                          style={resizeHandleDot('nesw-resize', { top: '-5px', right: '-5px' })}
                          onMouseDown={(e) => handlePageNumResizeStart(e, 'ne', pageNum, labelRef.current)}
                        />
                        <div
                          style={resizeHandleDot('nesw-resize', { bottom: '-5px', left: '-5px' })}
                          onMouseDown={(e) => handlePageNumResizeStart(e, 'sw', pageNum, labelRef.current)}
                        />
                        <div
                          style={resizeHandleDot('nwse-resize', { bottom: '-5px', right: '-5px' })}
                          onMouseDown={(e) => handlePageNumResizeStart(e, 'se', pageNum, labelRef.current)}
                        />
                      </>
                    )}
                  </div>
                );
              })()}

              {/* ── Overlays ── */}
              {overlays &&
                overlays
                  .filter(o => o.pdf_filename === activeFilename && o.page_number === pageNum)
                  .map((overlay) => {
                    const overlayWidth = overlay.width || (overlay.type === 'text' ? 0.35 : 0.2);
                    const overlayHeight = overlay.height || (overlay.type === 'text' ? 0.06 : 0.05);

                    return (
                      <div
                        key={overlay.id}
                        className={`overlay-item ${selectedOverlayId === overlay.id ? 'selected' : ''}`}
                        style={{
                          left: `${overlay.x * 100}%`,
                          top: `${overlay.y * 100}%`,
                          width: `${overlayWidth * 100}%`,
                          height: `${overlayHeight * 100}%`,
                          minWidth: 'unset',
                          position: 'absolute',
                          zIndex: 10,
                          transform: 'none',
                          transition: isDragging ? 'none' : 'all 0.15s ease-out',
                          willChange: isDragging ? 'left, top' : 'auto',
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onPageClick(pageNum);
                          onSelectOverlay(overlay.id);
                          const pageWrapper = e.currentTarget.closest('.page-wrapper');
                          if (pageWrapper) draggingOverlayPageRect.current = pageWrapper.getBoundingClientRect();
                          setIsDragging(true);
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            border: selectedOverlayId === overlay.id
                              ? (overlay.type === 'text' ? 'none' : '2px solid #3b82f6')
                              : '2px dashed transparent',
                            boxShadow:
                              selectedOverlayId === overlay.id && overlay.type !== 'text'
                                ? '0 0 0 1px rgba(59,130,246,0.3)'
                                : 'none',
                          }}
                        >
                          {overlay.type === 'text' ? (
                            <div
                              className="annexure-sticker"
                              style={{
                                fontSize: `${overlay.fontSize || 20}px`,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                              }}
                            >
                              {overlay.text}
                            </div>
                          ) : (
                            <img
                              src={
                                overlay.is_temp
                                  ? overlay.local_url
                                  : `${apiBaseUrl}/api/overlay-image/${sessionId}/${overlay.image_filename}`
                              }
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'fill',
                                pointerEvents: 'none',
                                display: 'block',
                              }}
                              alt="overlay"
                            />
                          )}

                          {selectedOverlayId === overlay.id && (
                            <>
                              <button
                                className="overlay-delete-btn"
                                style={{
                                  position: 'absolute', top: '-10px', right: '-10px',
                                  width: '20px', height: '20px', borderRadius: '50%',
                                  background: '#ef4444', border: '2px solid white',
                                  color: 'white', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  padding: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  zIndex: 1001, transition: 'all 0.2s',
                                }}
                                onClick={(e) => { e.stopPropagation(); onDeleteOverlay(overlay.id); }}
                              >
                                <XIcon size={12} />
                              </button>
                              <div style={resizeHandleDot('nwse-resize', { top: '-5px', left: '-5px' })}    onMouseDown={(e) => handleResizeStart(e, 'nw', overlay)} />
                              <div style={resizeHandleDot('nesw-resize', { top: '-5px', right: '-5px' })}   onMouseDown={(e) => handleResizeStart(e, 'ne', overlay)} />
                              <div style={resizeHandleDot('nesw-resize', { bottom: '-5px', left: '-5px' })} onMouseDown={(e) => handleResizeStart(e, 'sw', overlay)} />
                              <div style={resizeHandleDot('nwse-resize', { bottom: '-5px', right: '-5px' })} onMouseDown={(e) => handleResizeStart(e, 'se', overlay)} />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          );
        })}
      </Document>
    </div>
  );
}